import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { ColDef } from "ag-grid-community";
import { ThreeCircles } from "react-loader-spinner";
import * as Icon from "react-bootstrap-icons";
import { ToastContainer } from "react-toastify";
import ContentBlockEditorModal from "./components/ContentBlockEditorModal/ContentBlockEditorModal";
import { fetchContentBlocks } from "./ContentBlocksPage.service";
import { ContentBlockRecord, getGridStyle, ModelMode} from "./ContentBlocksPage.utils";
import ActionRenderer from "./components/ActionsRenderer/ActionsRenderer";
import styles from "./ContentBlocksPage.module.css";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import { TimeRenderer } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";

import '../EngagementLetter.css';

const ContentBlocksPage: React.FC = () => {
  // State variables
  const [refreshGrid, setRefreshGrid] = useState<boolean>(false);
  const [contentBlocksDetails, setContentBlocksDetails] = useState<ContentBlockRecord[]>([]);
  const [contentBlockModelShow, setContentBlockModelShow] = useState<boolean>(false);
  const [contentBlockId, setContentBlockId] = useState<number | string | undefined>();
  const [blockName, setBlockName] = useState<string>("");
  const [blockData, setBlockData] = useState<string>("");
  const [active, setActive] = useState<"Y" | "N">("Y");
  const [status, setStatus] = useState<string>("Active");
  const [modelMode, setModelMode] = useState<ModelMode>("create");
  const [key, setKey] = useState<number | undefined>(undefined);

  // Fetch content blocks data
  const fetchContentBlocksPageData = useCallback(async () => {
    resetState();
    setRefreshGrid(true);

    try {
      const details = await fetchContentBlocks();
      setContentBlocksDetails(details);
      setKey(details.length);
    } catch (error) {
    } finally {
      setRefreshGrid(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchContentBlocksPageData();
  }, []);

  // Column definitions for content blocks grid
  const defaulContentBlocksColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: "Block Name",
      field: "BlockName",
      sortable: false,
      flex: 3
    },
    {
      headerName: "Status",
      field: "Status",
      sortable: false,
      flex: 1
    },
    {
      headerName: "Updated By",
      field: "UpdatedBy",
      sortable: false,
      flex: 1
    },
    {
      headerName: "Created Date",
      field: "CreatedDate",
      cellRenderer: TimeRenderer,
      cellRendererParams: (params: any) => ({
          value: params.value,
      }),
      sortable: false,
      flex: 1
    },
    {
      headerName: "Updated Date",
      field: "UpdatedDate",
      cellRenderer: TimeRenderer,
      cellRendererParams: (params: any) => ({
          value: params.value,
      }),
      sortable: false,
      flex: 1
    },
    {
      headerName: "Action",
      field: "",
      pinned: "right",
      cellRenderer: (params: any) => (
        <ActionRenderer
          data={params.data}
          onView={(data) => handleContentBlockShow("view", data)}
          onEdit={(data) => handleContentBlockShow("edit", data)}
          onDelete={fetchContentBlocksPageData}
        />
      ),
      maxWidth: 90,
      minWidth: 90,
      sortable: false
    }
  ], []);

  const [contentBlocksColumnDefs, setContentBlocksColumnDefs] = useState<ColDef[]>(defaulContentBlocksColumnDefs);

  // Reset state function
  const resetState = () => {
    setRefreshGrid(false);
    setContentBlocksDetails([]);
    setContentBlocksColumnDefs(defaulContentBlocksColumnDefs);
    setContentBlockId(undefined);
    setBlockName("");
    setBlockData("");
    setActive("Y");
    setStatus("Active");
    setModelMode("create");
  };

  // Function to show content block modal
  const handleContentBlockShow = (mode: ModelMode, data: ContentBlockRecord | null = null) => {
    if (mode !== "create" && data) {
      setContentBlockId(data.ContentBlockId);
      setBlockName(data.BlockName);
      setBlockData(data.BlockData);
      setActive(data.Active!);
      setStatus(data.Status!);
    } else {
      setContentBlockId(undefined);
      setBlockName("");
      setBlockData("");
      setActive("Y");
      setStatus("Active");
    }
    setModelMode(mode);
    setContentBlockModelShow(true);
  };

  // Function to hide content block modal
  const handleContentBlockHide = (shouldRefresh: boolean | undefined) => {
    setContentBlockModelShow(false);
    if (shouldRefresh) {
      fetchContentBlocksPageData();
    }
  };

  // JSX for the page component
  return (
    <>
      {contentBlockModelShow && (
        <ContentBlockEditorModal
          mode={modelMode}
          contentBlockId={contentBlockId}
          blockName={blockName}
          blockData={blockData}
          active={active}
          status={status}
          onHide={handleContentBlockHide}
        />
      )}
      <Row className='no-gutter'>
        <p className={styles.pageTitle + " display-6"}>
          Content Blocks
        </p>
      </Row>
      <Row className='no-gutter'>
        <Col md={12} className="d-flex justify-content-end">
          <div className={styles.createButtonContainer}>
            <CustomButton
              content={"Create Content Block"}
              onclick={() => {
                handleContentBlockShow("create")
              }}
            />
          </div>
          <div className={styles.refreshContainer}>
            {refreshGrid ? (
              <div>
                <ThreeCircles
                  height="20"
                  width="20"
                  color="#4fa94d"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel="three-circles-rotating"
                  outerCircleColor="var(--theme1_tertiary)"
                  innerCircleColor="var(--theme1_secondary)"
                  middleCircleColor="var(--theme1_primary)"
                />
              </div>
            ) : (
              <Icon.ArrowCounterclockwise
                onClick={fetchContentBlocksPageData}
                className={styles.refreshIcon}
              />
            )}
          </div>
        </Col>
      </Row>
      <Row className='no-gutter'>
        <Col md={12}>
          <div className="ag-theme-balham" style={getGridStyle()}>
            <CygnusGrid
              key={key}
              fetchData={async () => {
                // Since this is not paginated, just return everything
                return { records: contentBlocksDetails, totalCount: contentBlocksDetails.length };
              }}
              rowModelType="clientSide"
              dataIdentifier="ContentBlockId"
              cachedData={contentBlocksDetails}
              setCachedData={setContentBlocksDetails}
              columnDefs={contentBlocksColumnDefs}
              rowSelection={"multiple"}
              cachedIsLoading={refreshGrid}
              onLoadingChange={setRefreshGrid}
            />
          </div>
        </Col>
      </Row>
      <ToastContainer />
    </>
  );
};

export default ContentBlocksPage;